import styled from "@emotion/styled";

import SkeletonText from "components/ui/skeleton/SkeletonText";

// Bar heights as a share of the plot: uneven, so it reads as a chart rather than a block.
const BAR_HEIGHTS = [0.52, 0.61, 0.57, 0.68, 0.49, 0.63, 0.72, 0.6, 0.8, 0.55, 0.66, 0.58];
const Y_TICKS = 5;
const X_TICKS = 6;
const TOP_PADDING = 36;
const X_AXIS_HEIGHT = 28;
const GAP = 12;

type Props = {
  /** The height of the chart it stands in for, so nothing moves when the chart arrives. */
  height: number;
  /** Read out in place of the chart while it loads. Leave out when a larger placeholder already announces the wait. */
  label?: string;
};

/**
 * A chart-shaped placeholder: axis labels and a row of bars. It stands in for every chart
 * type, since it only has to say "a chart is coming", not which kind.
 */
export function ChartSkeleton({ height, label }: Props) {
  const plotHeight = height - TOP_PADDING - X_AXIS_HEIGHT - GAP;
  return (
    <Grid
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      frameHeight={height}
    >
      <YAxis>
        {[...Array(Y_TICKS)].map((_, index) => (
          <SkeletonText key={index} width="28px" fontSize={12} customSpacing shimmer />
        ))}
      </YAxis>
      <Plot>
        {BAR_HEIGHTS.map((share, index) => (
          <SkeletonText
            key={index}
            width="100%"
            height={Math.round(plotHeight * share)}
            variant="rectangular"
            customSpacing
            shimmer
          />
        ))}
      </Plot>
      <div />
      <XAxis>
        {[...Array(X_TICKS)].map((_, index) => (
          <SkeletonText key={index} width="44px" fontSize={12} customSpacing shimmer />
        ))}
      </XAxis>
    </Grid>
  );
}

const Grid = styled.div<{ frameHeight: number }>(({ frameHeight }) => ({
  display: "grid",
  gridTemplateColumns: "44px 1fr",
  gridTemplateRows: `1fr ${X_AXIS_HEIGHT}px`,
  gap: GAP,
  width: "100%",
  height: frameHeight,
  // The padding sits inside the chart's height, so the placeholder never outgrows its frame.
  boxSizing: "border-box",
  paddingTop: TOP_PADDING,
  paddingRight: 8,
}));

const YAxis = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-end",
  paddingBottom: 4,
});

const Plot = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: "2.6%",
  padding: "0 1.2%",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const XAxis = styled.div({
  display: "flex",
  justifyContent: "space-between",
  padding: "0 3%",
});
